import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Phone, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

export function PhoneIntegrationSettings() {
  const { activeShop } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [callRecordingEnabled, setCallRecordingEnabled] = useState(false);
  const [callPlaybackTechsEnabled, setCallPlaybackTechsEnabled] = useState(true);

  useEffect(() => {
    if (!activeShop?.id) return;

    const fetchSettings = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('shop_profile')
        .select('call_recording_enabled, call_playback_techs_enabled')
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      if (!error && data) {
        setCallRecordingEnabled(data.call_recording_enabled ?? false);
        setCallPlaybackTechsEnabled(data.call_playback_techs_enabled ?? true);
      }
      setLoading(false);
    };

    fetchSettings();
  }, [activeShop?.id]);

  const updateSetting = async (field: string, value: boolean) => {
    if (!activeShop?.id) return;
    setSaving(true);

    const { error } = await supabase
      .from('shop_profile')
      .update({ [field]: value })
      .eq('shop_id', activeShop.id);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to update phone integration settings.',
        variant: 'destructive',
      });
    } else {
      toast({ title: 'Settings updated' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Phone Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Phone Integration
        </CardTitle>
        <CardDescription>
          Configure call recording ingestion and playback access for your shop.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Call Recording Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="call-recording" className="text-sm font-medium">
              Enable Call Recording Ingestion
            </Label>
            <p className="text-xs text-muted-foreground">
              When enabled, incoming call data from your VoIP provider will be attached to tickets.
            </p>
          </div>
          <Switch
            id="call-recording"
            checked={callRecordingEnabled}
            disabled={saving}
            onCheckedChange={(checked) => {
              setCallRecordingEnabled(checked);
              updateSetting('call_recording_enabled', checked);
            }}
          />
        </div>

        {/* Technician Playback Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="tech-playback" className="text-sm font-medium">
              Enable Playback Access for Technicians
            </Label>
            <p className="text-xs text-muted-foreground">
              Allow assigned technicians to play back call recordings on their tickets.
            </p>
          </div>
          <Switch
            id="tech-playback"
            checked={callPlaybackTechsEnabled}
            disabled={saving}
            onCheckedChange={(checked) => {
              setCallPlaybackTechsEnabled(checked);
              updateSetting('call_playback_techs_enabled', checked);
            }}
          />
        </div>

        {/* Compliance Disclosure */}
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Call recording consent compliance is the responsibility of the shop. Ensure you comply 
            with all applicable federal, state, and local laws regarding call recording and consent 
            before enabling this feature.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
